import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CategoryOption {
  value: string;
  label: string;
  icon?: string;
  type: 'income' | 'expense';
}

export interface CategoryGroupOption {
  label: string;
  type: 'income' | 'expense';
  categories: CategoryOption[];
}

interface CategoryMultiSelectProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  groups: CategoryGroupOption[];
  placeholder?: string;
  incomeLabel?: string;
  expenseLabel?: string;
  countLabel?: (n: number) => string;
  className?: string;
}

interface DropdownPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  openUp: boolean;
}

export function CategoryMultiSelect({
  selectedIds,
  onChange,
  groups,
  placeholder = '',
  incomeLabel = 'Revenus',
  expenseLabel = 'Dépenses',
  countLabel,
  className,
}: CategoryMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({ left: 0, width: 0, openUp: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const allCategories = groups.flatMap((g) => g.categories);
  const selectedCount = selectedIds.length;
  const allSelected = selectedCount === 0;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 6;
    const maxDropdownHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;

    if (openUp) {
      setPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: Math.max(rect.width, 260),
        openUp: true,
      });
    } else {
      setPosition({
        top: rect.bottom + gap,
        left: rect.left,
        width: Math.max(rect.width, 260),
        openUp: false,
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        listRef.current &&
        !listRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleCategory = (id: string) => {
    if (selectedIds.includes(id)) {
      const next = selectedIds.filter((s) => s !== id);
      onChange(next);
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const toggleGroupType = (type: 'income' | 'expense') => {
    const typeIds = allCategories.filter((c) => c.type === type).map((c) => c.value);
    const allTypeSelected = typeIds.every((id) => selectedIds.includes(id));
    if (allTypeSelected) {
      onChange(selectedIds.filter((id) => !typeIds.includes(id)));
    } else {
      const merged = new Set([...selectedIds, ...typeIds]);
      onChange(Array.from(merged));
    }
  };

  const selectAll = () => {
    onChange([]);
  };

  const getLabel = () => {
    if (selectedCount === 0) return placeholder;
    if (selectedCount === 1) {
      return allCategories.find((c) => c.value === selectedIds[0])?.label ?? placeholder;
    }
    return countLabel ? countLabel(selectedCount) : `${selectedCount} catégories`;
  };

  // Group by type sections: income first, then expense
  const incomeGroups = groups.filter((g) => g.type === 'income');
  const expenseGroups = groups.filter((g) => g.type === 'expense');

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      } else if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
        e.preventDefault();
        updatePosition();
        setOpen(true);
      }
    },
    [open, updatePosition]
  );

  const renderSection = (
    sectionLabel: string,
    type: 'income' | 'expense',
    sectionGroups: CategoryGroupOption[]
  ) => {
    const typeIds = allCategories.filter((c) => c.type === type).map((c) => c.value);
    const allTypeSelected = typeIds.length > 0 && typeIds.every((id) => selectedIds.includes(id));

    return (
      <div key={type}>
        <div
          className="flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          onClick={() => toggleGroupType(type)}
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {sectionLabel}
          </span>
          {allTypeSelected && <Check className="h-3 w-3 text-[#16c47f]" />}
        </div>
        {sectionGroups.map((group) => (
          <div key={group.label}>
            <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-300 dark:text-gray-600 pl-4">
              {group.label}
            </div>
            {group.categories.map((cat) => {
              const isSelected = selectedIds.includes(cat.value);
              return (
                <div
                  key={cat.value}
                  onClick={() => toggleCategory(cat.value)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 pl-5 cursor-pointer text-sm transition-colors',
                    isSelected
                      ? 'bg-[#16c47f]/10 text-[#16c47f] dark:bg-[#16c47f]/20 font-medium'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  )}
                >
                  {cat.icon && <span className="text-xs">{cat.icon}</span>}
                  <span className="truncate flex-1 text-[13px]">{cat.label}</span>
                  {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-[#16c47f]" />}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const dropdown = open
    ? createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed',
            ...(position.openUp ? { bottom: position.bottom } : { top: position.top }),
            left: position.left,
            width: position.width,
            zIndex: 99999,
          }}
          className={cn(
            'overflow-auto rounded-xl',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-600',
            'shadow-xl shadow-black/10 dark:shadow-black/30',
            'max-h-80 py-1'
          )}
        >
          {/* "All" option */}
          <div
            onClick={selectAll}
            className={cn(
              'flex items-center justify-between px-3 py-2 cursor-pointer text-sm transition-colors border-b border-gray-100 dark:border-gray-700',
              allSelected
                ? 'bg-[#16c47f]/10 text-[#16c47f] font-medium'
                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            )}
          >
            <span className="text-[13px]">{placeholder}</span>
            {allSelected && <Check className="h-3.5 w-3.5 shrink-0 text-[#16c47f]" />}
          </div>

          {incomeGroups.length > 0 && renderSection(incomeLabel, 'income', incomeGroups)}
          {expenseGroups.length > 0 && renderSection(expenseLabel, 'expense', expenseGroups)}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="relative" onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          if (!open) updatePosition();
          setOpen(!open);
        }}
        className={cn(
          'flex w-full items-center justify-between px-4 py-3 text-left',
          'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl',
          'text-gray-900 dark:text-white text-sm',
          'focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]',
          'transition-all duration-150',
          open && 'ring-2 ring-[#16c47f]/30 border-[#16c47f]',
          className
        )}
      >
        <span className={cn('truncate', allSelected && 'text-gray-400 dark:text-gray-500')}>
          {getLabel()}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ml-2',
            open && 'rotate-180'
          )}
        />
      </button>
      {dropdown}
    </div>
  );
}
